import { forwardRef, type ReactNode, type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/ui/icon';
import {
  statusToneTextClass,
  statusToneIconClass,
  statusToneClasses,
  type StatusTone,
} from '@/lib/statusTone';

export type HeaderStatusPillProps = Omit<ComponentPropsWithoutRef<'button'>, 'children'> & {
  /** Semantic tone for coloring */
  tone: StatusTone;
  /** Material icon name */
  icon: Parameters<typeof Icon>[0]['name'];
  /** Label text */
  label: string;
  /** Accessibility label override */
  ariaLabel?: string;
  /** Additional class for the icon (e.g., for animations) */
  iconClassName?: string;
  /** Children to render after the label (e.g., tooltip trigger wrapper) */
  children?: ReactNode;
};

/**
 * Consistent status pill for the header bar.
 * Used for sync status, safe mode indicator, etc.
 * Forwards ref for Radix asChild compatibility.
 */
export const HeaderStatusPill = forwardRef<HTMLButtonElement, HeaderStatusPillProps>(
  function HeaderStatusPill(
    { tone, icon, label, ariaLabel, iconClassName, className, ...props },
    ref
  ) {
    const tokens = statusToneClasses(tone);

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex items-center gap-1.5 rounded-md border px-2 py-1',
          'text-xs font-medium transition-colors',
          tokens.subtleBorder,
          statusToneTextClass(tone),
          'hover:opacity-80',
          className
        )}
        aria-label={ariaLabel ?? label}
        {...props}
      >
        <Icon
          name={icon}
          className={cn('icon-sm', statusToneIconClass(tone), iconClassName)}
          aria-hidden
        />
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  }
);
